import { useState, useCallback } from "react";
import { useLocation } from "wouter";
import { ConnectionMapping } from "../types";
import { getWizardSkipIndices } from "../sections/NewConnection/wizardConfig";

export function useWizardSteps(totalSteps: number, skipIndices: Set<number> = new Set()) {
  const [, setLocation] = useLocation();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [highestVisitedIndex, setHighestVisitedIndex] = useState(0);

  const resolveSkip = (mappingsOverride?: ConnectionMapping[]): Set<number> =>
    Array.isArray(mappingsOverride) ? getWizardSkipIndices(mappingsOverride) : skipIndices;

  const nextVisibleIndex = (index: number, direction: 1 | -1, skip: Set<number>): number => {
    let i = index;
    while (i >= 0 && i < totalSteps && skip.has(i)) {
      i += direction;
    }
    return i;
  };

  const goToStep = useCallback(
    (index: number) => {
      if (index >= 0 && index <= highestVisitedIndex && !skipIndices.has(index)) {
        setCurrentStepIndex(index);
      }
    },
    [highestVisitedIndex, skipIndices]
  );

  const goBack = useCallback((mappingsOverride?: ConnectionMapping[]) => {
    const prevIndex = nextVisibleIndex(currentStepIndex - 1, -1, resolveSkip(mappingsOverride));
    if (prevIndex < 0) {
      setLocation("/koppelingen");
      return;
    }
    setCurrentStepIndex(prevIndex);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- listing this would re-run the effect on changes it should ignore
  }, [currentStepIndex, setLocation, skipIndices, totalSteps]);

  const goNext = useCallback((mappingsOverride?: ConnectionMapping[]) => {
    const nextIndex = nextVisibleIndex(currentStepIndex + 1, 1, resolveSkip(mappingsOverride));
    if (nextIndex < totalSteps) {
      setCurrentStepIndex(nextIndex);
      setHighestVisitedIndex((prev) => Math.max(prev, nextIndex));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- listing this would re-run the effect on changes it should ignore
  }, [currentStepIndex, totalSteps, skipIndices]);

  return {
    currentStepIndex,
    highestVisitedIndex,
    goToStep,
    goBack,
    goNext,
  };
}
